/**
 * Product attribute-assignment admin (SPEC screen 2, Variants tab — the
 * deferred assign/unassign surface). Lists the product's assignments in
 * `position` order joined to the global definitions, toggles
 * `is_required` / `is_variant_axis` via merge-PATCH guarded by the
 * ASSIGNMENT's own If-Match (a fresh detail GET captures the validator),
 * assigns unassigned definitions (POST + Idempotency-Key) and unassigns via
 * the guarded DELETE. Contract failure modes (422 `attribute_in_use`, …)
 * render inline — never toast-only.
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Trash2 } from "lucide-react";
import { Badge, Button, Checkbox, Combobox, Label } from "@erp/ui";
import { useMutation } from "@tanstack/react-query";

import { surfaceProblem } from "@/components/form";
import { ApiError } from "@/lib/api/errors";
import { useIdempotentMutation } from "@/features/shared/ops-api";

import {
  assignProductAttribute,
  getProductAttribute,
  unassignProductAttribute,
  updateProductAttribute,
  type Attribute,
  type ProductAttribute,
  type ProductAttributePatch,
} from "../api";

type PanelError =
  | { kind: "inUse"; attributeId?: string; count?: number; detail?: string }
  | { kind: "problem"; title?: string; detail?: string };

export interface AttributesPanelProps {
  productId: string;
  /** The product's assignments (GET /products/{id}/attributes). */
  assignments: ProductAttribute[];
  /** Global attribute definitions (code/type/options lookup + add picker). */
  definitions: Attribute[];
  /** Invalidate the assignments query — the variant-grid columns react. */
  onChanged: () => void;
  canManage?: boolean;
}

export function AttributesPanel({
  productId,
  assignments,
  definitions,
  onChanged,
  canManage = true,
}: AttributesPanelProps) {
  const { t } = useTranslation();
  const [pickedId, setPickedId] = useState<string | null>(null);
  const [error, setError] = useState<PanelError | null>(null);

  const definitionOf = useMemo(
    () => new Map(definitions.map((definition) => [definition.id, definition])),
    [definitions],
  );

  const rows = useMemo(
    () =>
      [...assignments].sort(
        (a, b) => a.position - b.position || a.attribute_id.localeCompare(b.attribute_id),
      ),
    [assignments],
  );

  // Add picker offers only ACTIVE definitions not yet assigned.
  const available = useMemo(() => {
    const assigned = new Set(assignments.map((assignment) => assignment.attribute_id));
    return definitions
      .filter((definition) => definition.status === "active" && !assigned.has(definition.id))
      .map((definition) => ({
        value: definition.id,
        label: `${definition.code} — ${definition.name}`,
      }));
  }, [assignments, definitions]);

  const surface = (err: unknown) => {
    if (err instanceof ApiError) {
      const problem = err.problem as
        | { title?: string; detail?: string; attribute_id?: string; usage_count?: number }
        | undefined;
      if (err.code === "attribute_in_use") {
        setError({
          kind: "inUse",
          attributeId: problem?.attribute_id,
          count: problem?.usage_count,
          detail: problem?.detail,
        });
        return;
      }
      setError({ kind: "problem", title: problem?.title, detail: problem?.detail });
      return;
    }
    // Non-problem failures (network, …) keep the shared toast surface.
    surfaceProblem(err, t);
  };

  const assignMutation = useIdempotentMutation({
    mutationFn: (attributeId: string, key: string) =>
      assignProductAttribute(
        productId,
        {
          attribute_id: attributeId,
          is_required: false,
          is_variant_axis: false,
          position: rows.reduce((max, row) => Math.max(max, row.position + 1), 0),
        },
        key,
      ),
    onSuccess: () => {
      setPickedId(null);
      setError(null);
      onChanged();
    },
    onError: surface,
  });

  const patchMutation = useMutation({
    mutationFn: async (input: { attributeId: string; patch: ProductAttributePatch }) => {
      // PATCH requires the ASSIGNMENT's own ETag — a fresh detail GET
      // captures the current validator right before the write.
      const { etag } = await getProductAttribute(productId, input.attributeId);
      if (etag === null) {
        throw new ApiError("Missing assignment ETag; reload and retry", 428);
      }
      return updateProductAttribute(productId, input.attributeId, input.patch, etag);
    },
    onSuccess: () => {
      setError(null);
      onChanged();
    },
    onError: surface,
  });

  const removeMutation = useMutation({
    mutationFn: (attributeId: string) => unassignProductAttribute(productId, attributeId),
    onSuccess: () => {
      setError(null);
      onChanged();
    },
    onError: surface,
  });

  const mutating = assignMutation.isPending || patchMutation.isPending || removeMutation.isPending;

  const codeOf = (attributeId: string | undefined): string =>
    (attributeId !== undefined ? definitionOf.get(attributeId)?.code : undefined) ??
    attributeId ??
    "";

  return (
    <section className="flex flex-col gap-3">
      <div>
        <h2 className="text-sm font-semibold">
          {t("catalog.editor.attributes.title", "Attributes")}
        </h2>
        <p className="text-xs text-muted-foreground">
          {t(
            "catalog.editor.attributes.hint",
            "Assigned attributes drive the variant grid columns.",
          )}
        </p>
      </div>

      <div className="w-full overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <th scope="col" className="p-2 text-start font-medium">
                {t("catalog.editor.attributes.columns.attribute", "Attribute")}
              </th>
              <th scope="col" className="p-2 text-start font-medium">
                {t("catalog.editor.attributes.columns.type", "Type")}
              </th>
              <th scope="col" className="p-2 text-center font-medium">
                {t("catalog.editor.attributes.columns.required", "Required")}
              </th>
              <th scope="col" className="p-2 text-center font-medium">
                {t("catalog.editor.attributes.columns.axis", "Variant axis")}
              </th>
              <th scope="col" className="p-2" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="p-6 text-center text-sm text-muted-foreground">
                  {t("catalog.editor.attributes.empty", "No attributes assigned yet.")}
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const definition = definitionOf.get(row.attribute_id);
                const code = definition?.code ?? row.attribute_id;
                return (
                  <tr key={row.attribute_id} className="border-b">
                    <td className="p-2">
                      <span className="flex items-center gap-2">
                        {/* Attribute codes are machine slugs — LTR. */}
                        <span dir="ltr" className="font-mono">
                          {code}
                        </span>
                        {definition !== undefined ? (
                          <span className="text-xs text-muted-foreground">{definition.name}</span>
                        ) : null}
                      </span>
                    </td>
                    <td className="p-2">
                      {definition !== undefined ? (
                        <Badge variant="outline">
                          {t(`catalog.attributes.type.${definition.type}`, definition.type)}
                        </Badge>
                      ) : null}
                    </td>
                    <td className="p-2 text-center">
                      <Checkbox
                        checked={row.is_required}
                        disabled={mutating || !canManage}
                        aria-label={t(
                          "catalog.editor.attributes.requiredFor",
                          "Required — {{code}}",
                          { code },
                        )}
                        onCheckedChange={(checked) =>
                          patchMutation.mutate({
                            attributeId: row.attribute_id,
                            patch: { is_required: checked === true },
                          })
                        }
                      />
                    </td>
                    <td className="p-2 text-center">
                      <Checkbox
                        checked={row.is_variant_axis}
                        disabled={mutating || !canManage}
                        aria-label={t(
                          "catalog.editor.attributes.axisFor",
                          "Variant axis — {{code}}",
                          { code },
                        )}
                        onCheckedChange={(checked) =>
                          patchMutation.mutate({
                            attributeId: row.attribute_id,
                            patch: { is_variant_axis: checked === true },
                          })
                        }
                      />
                    </td>
                    <td className="p-2 text-end">
                      {canManage ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          aria-label={t(
                            "catalog.editor.attributes.removeFor",
                            "Unassign {{code}}",
                            {
                              code,
                            },
                          )}
                          disabled={mutating}
                          onClick={() => removeMutation.mutate(row.attribute_id)}
                        >
                          <Trash2 className="size-4" aria-hidden="true" />
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {error !== null ? (
        <div
          role="alert"
          className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive"
        >
          {error.kind === "inUse" ? (
            <span>
              {error.count !== undefined
                ? t(
                    "catalog.editor.attributes.inUse",
                    "{{code}} cannot be unassigned — {{count}} variants still carry values for it.",
                    { code: codeOf(error.attributeId), count: error.count },
                  )
                : (error.detail ??
                  t(
                    "catalog.editor.attributes.inUseFallback",
                    "The attribute is still in use by variants.",
                  ))}
            </span>
          ) : (
            <span>
              {error.detail ??
                error.title ??
                t(
                  "catalog.editor.attributes.updateFailed",
                  "The attribute assignment could not be updated.",
                )}
            </span>
          )}
        </div>
      ) : null}

      {canManage ? (
        <div className="flex flex-wrap items-end gap-3">
          {available.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t(
                "catalog.editor.attributes.noneLeft",
                "All attribute definitions are already assigned.",
              )}
            </p>
          ) : (
            <>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="attributes-picker">
                  {t("catalog.editor.attributes.pick", "Attribute definition")}
                </Label>
                <Combobox
                  id="attributes-picker"
                  options={available}
                  value={pickedId}
                  onChange={(next) => {
                    setPickedId(next);
                    setError(null);
                  }}
                  placeholder={t("catalog.editor.attributes.pickPlaceholder", "Search attributes…")}
                  searchPlaceholder={t(
                    "catalog.editor.attributes.pickSearch",
                    "Search attributes…",
                  )}
                  emptyMessage={t("catalog.editor.attributes.pickEmpty", "No matching attribute.")}
                  className="w-56"
                />
              </div>
              <Button
                type="button"
                onClick={() => {
                  if (pickedId !== null) assignMutation.mutate(pickedId);
                }}
                disabled={pickedId === null || assignMutation.isPending}
                loading={assignMutation.isPending}
              >
                {t("catalog.editor.attributes.add", "Assign attribute")}
              </Button>
            </>
          )}
        </div>
      ) : null}
    </section>
  );
}
